import * as QueryKeys from "data";
import { fetchAddressById, fetchPredictive } from "data/queries-common";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Address, AddressResult } from "./models";
import { PredictiveAddressManager } from "./predictive-address-manager";

export const AddressEntryForm = (
  predictiveManager: PredictiveAddressManager,
) => {
  const [inputValue, setInputValue] = useState("");
  const [addressId, setAddressId] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const debouncedSearch = useCallback(
    _.debounce((searchTerm: string) => {
      predictiveManager.setQuery(searchTerm);
    }, predictiveManager.debounce),
    [predictiveManager.debounce],
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowSuggestions(true);
    debouncedSearch(value);
  };

  const handleSelect = (addressResult: AddressResult) => {
    predictiveManager.setSuggest(null);
    setAddressId(addressResult.id);
    setInputValue(addressResult.address);
    setShowSuggestions(false);
  };

  const predictiveQuery = useQuery({
    queryKey: [QueryKeys.predictiveAddress, predictiveManager.query],
    queryFn: () =>
      fetchPredictive(
        predictiveManager.query,
        predictiveManager.dataset.join(","),
        predictiveManager.excludeAliases,
        predictiveManager.addressType.join(","),
        predictiveManager.stateFilter.join(","),
        predictiveManager.maxResults,
        predictiveManager.excludePrincipalWithSec,
      ),
    enabled: !!predictiveManager.query,
  });

  useEffect(() => {
    if (predictiveQuery.isSuccess && predictiveQuery.data) {
      predictiveManager.setSuggest(predictiveQuery.data);
      predictiveManager.setCallCounter(predictiveManager.callCounter + 1);
      predictiveManager.setCreditCounter(
        predictiveManager.creditCounter + predictiveQuery.data.basictransaction,
      );
    }
  }, [predictiveQuery.isSuccess]);

  const addressByIdQuery = useQuery({
    queryKey: [QueryKeys.addressById, addressId, predictiveManager.crs],
    queryFn: () => fetchAddressById(addressId, predictiveManager.crs),
    enabled: !!addressId,
  });

  useEffect(() => {
    if (addressByIdQuery.isSuccess && addressByIdQuery.data) {
      predictiveManager.setAddress(addressByIdQuery.data);
      predictiveManager.setCallCounter(predictiveManager.callCounter + 1);
      predictiveManager.setCreditCounter(
        predictiveManager.creditCounter +
          addressByIdQuery.data.basictransaction,
      );
    }
  }, [addressByIdQuery.isSuccess]);

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${_.escapeRegExp(query)})`, "gi"));
    return parts.map((part, index) => (
      <span
        key={index}
        className={
          part.toLowerCase() === query.toLowerCase() ? "font-bold" : ""
        }
      >
        {part}
      </span>
    ));
  };

  const constructAddressLine1 = (address: Address) => {
    const p = address.address.properties;
    return [p.street_number_1, p.street_number_2, p.street_name, p.street_type]
      .filter((v) => !!v)
      .join(" ")
      .trim();
  };

  const constructAddressLine2 = (address: Address) => {
    const p = address.address.properties;
    return [
      p.site_name,
      p.complex_unit_type,
      p.complex_unit_identifier,
      p.complex_level_type,
      p.complex_level_number,
    ]
      .filter((v) => !!v)
      .join(" ")
      .trim();
  };

  const suggestions = predictiveQuery.data?.suggest ?? [];
  const addressLine1 = predictiveManager.address
    ? constructAddressLine1(predictiveManager.address)
    : "";
  const addressLine2 = predictiveManager.address
    ? constructAddressLine2(predictiveManager.address)
    : "";
  const suburb =
    predictiveManager.address?.address.properties.locality_name ?? "";
  const stateTerritory =
    predictiveManager.address?.address.properties.state_territory ?? "";
  const postcode = predictiveManager.address?.address.properties.postcode ?? "";

  return (
    <Card className="overflow-visible">
      <CardHeader>
        <CardTitle>Address Form</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="address-search">Search</Label>
          <Popover
            open={showSuggestions && suggestions.length > 0}
            onOpenChange={(open) => {
              if (open) setShowSuggestions(true);
              else if (document.activeElement?.id !== "address-search") {
                setShowSuggestions(false);
              }
            }}
          >
            <PopoverAnchor asChild>
              <div>
                <Input
                  id="address-search"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onFocus={() =>
                    suggestions.length > 0 && setShowSuggestions(true)
                  }
                  placeholder="Start typing to get auto-complete suggestions from the API"
                  autoComplete="off"
                />
              </div>
            </PopoverAnchor>
            <PopoverContent
              className="w-(--radix-popover-trigger-width) p-0"
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={(e) => {
                if (
                  e.target instanceof Element &&
                  e.target.closest("#address-search")
                ) {
                  e.preventDefault();
                }
              }}
            >
              <ul className="max-h-60 overflow-y-auto py-1">
                {suggestions.map((option) => (
                  <li
                    key={option.id}
                    className="px-3 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                  >
                    {highlightText(option.address, inputValue)}
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-1 px-2 py-1 border-t text-xs text-muted-foreground">
                <span>Powered by</span>
                <span className="font-bold">Geoscape</span>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="addressLine1" className="text-xs">
            Address Line 1
          </Label>
          <Input
            id="addressLine1"
            value={addressLine1}
            disabled={!predictiveManager.address}
            readOnly
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="addressLine2" className="text-xs">
            Address Line 2
          </Label>
          <Input
            id="addressLine2"
            value={addressLine2}
            disabled={!predictiveManager.address}
            readOnly
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="suburb" className="text-xs">
            Suburb
          </Label>
          <Input
            id="suburb"
            value={suburb}
            disabled={!predictiveManager.address}
            readOnly
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="stateTerritory" className="text-xs">
              State/Territory
            </Label>
            <Input
              id="stateTerritory"
              value={stateTerritory}
              disabled={!predictiveManager.address}
              readOnly
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="postcode" className="text-xs">
              Postcode
            </Label>
            <Input
              id="postcode"
              value={postcode}
              disabled={!predictiveManager.address}
              readOnly
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
